import React, { useEffect, useState, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BoxIcon, CalendarIcon, CreditCardIcon, HelpCircleIcon, MessageSquareIcon, ArchiveIcon, HeartIcon, ShoppingBagIcon, GlobeIcon, StarIcon, ImageIcon } from 'lucide-react';
import { bookBoxes } from '../../BookBoxDetails';
import { getPastBoxesByName, getUpcomingBoxesByName } from './data';
import { OverviewTab } from './OverviewTab';
import { PastBoxesTab } from './PastBoxesTab';
import { UpcomingBoxesTab } from './UpcomingBoxesTab';
import { SubscribeTab } from './SubscribeTab';
import { FAQsTab } from './FAQsTab';
import { ReviewsTab } from './ReviewsTab';
import { GalleryTab } from './GalleryTab';
import ProfileLayout, { ProfileData } from '../../components/Profile/ProfileLayout';
import type { BookBox } from './types';
const BookBoxDetail = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [bookBox, setBookBox] = useState<BookBox | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [expandedPastBox, setExpandedPastBox] = useState<number | null>(null);
  const [subscriptionLength, setSubscriptionLength] = useState('1 Box');
  const [activeTab, setActiveTab] = useState('overview');
  const [trackedReleases, setTrackedReleases] = useState<number[]>([]);
  useEffect(() => {
    if (id) {
      const decodedBoxName = decodeURIComponent(id);
      const foundBox = bookBoxes.find(box => box.boxName === decodedBoxName);
      if (foundBox) {
        setBookBox(foundBox);
        if (foundBox.customizationOptions && foundBox.customizationOptions.length > 0) {
          setSelectedOption(foundBox.customizationOptions[0]);
        }
      } else {
        navigate('/book-boxes');
      }
    }
  }, [id, navigate]);
  const toggleReleaseTracking = (releaseId: number) => {
    setTrackedReleases(prev => prev.includes(releaseId) ? prev.filter(id => id !== releaseId) : [...prev, releaseId]);
  };
  if (!bookBox) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-6782" data-mp="28ktxp04zhu4d">
        <div className="text-center" data-id="element-6783" data-mp="28iqvlhqk4aja">
          <BoxIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-6784" data-mp="24kb3x0jj6dzj" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-6785" data-mp="319sf1v1a8s8m">
            Loading book box...
          </h2>
        </div>
      </div>;
  }
  const relevantPastBoxes = getPastBoxesByName(bookBox.boxName);
  const relevantUpcomingBoxes = getUpcomingBoxesByName(bookBox.boxName);
  // Map BookBox data to ProfileData shape
  const profileData: ProfileData = {
    id: bookBox.boxName.toLowerCase().replace(/\s+/g, '-'),
    name: bookBox.boxName,
    role: `${bookBox.frequency} Book Box`,
    avatar: bookBox.curatorImage,
    coverImage: bookBox.curatorImage,
    status: 'Active',
    queueStatus: 'open',
    slotsAvailable: 450,
    maxSlots: 500,
    estimatedWait: bookBox.frequency === 'Monthly' ? 'Ships monthly' : bookBox.frequency,
    rating: bookBox.ratings?.average ?? 4.5,
    location: bookBox.locationCountry,
    bio: bookBox.description,
    specialties: bookBox.tags.slice(0, 4),
    stats: [{
      label: 'Rating',
      value: bookBox.ratings?.average ?? '—'
    }, {
      label: 'Reviews',
      value: bookBox.ratings?.count ?? '—'
    }, {
      label: 'Price',
      value: `${bookBox.currency} $${bookBox.price}`
    }, {
      label: 'Ships To',
      value: bookBox.shipping
    }],
    social: {
      website: bookBox.subscriptionUrl
    }
  };
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: BoxIcon
  }, {
    id: 'past-boxes',
    label: 'Past Boxes',
    icon: ArchiveIcon,
    count: relevantPastBoxes.length || undefined
  }, {
    id: 'upcoming',
    label: 'Upcoming',
    icon: CalendarIcon,
    count: relevantUpcomingBoxes.length || undefined
  }, {
    id: 'subscribe',
    label: 'Subscribe',
    icon: CreditCardIcon
  }, {
    id: 'faqs',
    label: 'FAQs',
    icon: HelpCircleIcon
  }, {
    id: 'reviews',
    label: 'Reviews',
    icon: MessageSquareIcon
  }, {
    id: 'gallery',
    label: 'Gallery',
    icon: ImageIcon
  }];
  const actionButtons = <>
      <button onClick={() => setActiveTab('subscribe')} className="px-6 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 transition-colors shadow-lg shadow-purple-900/20 flex items-center gap-2" data-id="element-6786" data-mp="16uyab3hvw3l4">
        <ShoppingBagIcon className="w-4 h-4" data-id="element-6787" data-mp="1gr9s2908tlmy" />
        Subscribe Now
      </button>
      <button className="px-6 py-2.5 rounded-lg text-sm font-bold border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800 transition-colors flex items-center gap-2" data-id="element-6788" data-mp="t1yx585kqlgq">
        <HeartIcon className="w-4 h-4" data-id="element-6789" data-mp="2zqhc2ajksefc" />
        Save
      </button>
    </>;
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab bookBox={bookBox} data-id="element-6790" data-mp="2qr3zh905n9qe" />;
      case 'past-boxes':
        return <PastBoxesTab relevantPastBoxes={relevantPastBoxes} expandedPastBox={expandedPastBox} setExpandedPastBox={setExpandedPastBox} data-id="element-6791" data-mp="2usfkt2bzx8ss" />;
      case 'upcoming':
        return <UpcomingBoxesTab relevantUpcomingBoxes={relevantUpcomingBoxes} trackedReleases={trackedReleases} toggleReleaseTracking={toggleReleaseTracking} data-id="element-6792" data-mp="3ng1yj3extttd" />;
      case 'subscribe':
        return <SubscribeTab bookBox={bookBox} subscriptionLength={subscriptionLength} setSubscriptionLength={setSubscriptionLength} selectedOption={selectedOption} setSelectedOption={setSelectedOption} data-id="element-6793" data-mp="3ei9jm0d4oo6i" />;
      case 'faqs':
        return <FAQsTab expandedFaq={expandedFaq} setExpandedFaq={setExpandedFaq} data-id="element-6794" data-mp="3m86ew852vi0" />;
      case 'reviews':
        return <ReviewsTab bookBox={bookBox} data-id="element-6795" data-mp="3v049lj7jprtp" />;
      case 'gallery':
        return <GalleryTab data-id="element-6796" data-mp="3uy9n40gmlrni" />;
      default:
        return <OverviewTab bookBox={bookBox} data-id="element-6797" data-mp="3qxzgcll0ex7l" />;
    }
  };
  return <ProfileLayout profile={profileData} tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} actionButtons={actionButtons} data-id="element-6798" data-mp="3lzxw3xbukruj">
      {renderTabContent()}
    </ProfileLayout>;
};
export default BookBoxDetail;